import { type FormMessages } from '@lingoda/utils';
import { type FormValues } from '@lingoda/forms';
import { actionClick } from '@lingoda/analytics';
import { trackEverflowRegistration } from '@lingoda/analytics-everflow';
import { useUiEvent } from '@lingoda/ui';
import { createStudent } from '@lingoda/auth';
import { goTo, redirect, studentHomePath, useRedirectPath } from '@lingoda/router';
import { showToast } from '@lingoda/toasts';
import { useDispatch } from '@lingoda/hooks';
import { isCaptchaError, useGoogleRecaptchaV3 } from '@lingoda/recaptcha';
import { useRequestTracker } from '@lingoda/core';
import { type SectionName } from '@lingoda/sections';
import { type RegisterQueryParams } from '../types';

export const useRegisterForm = ({
    registerParams,
}: {
    registerParams: RegisterQueryParams & { section: SectionName };
}) => {
    const dispatch = useDispatch();
    const trackRequest = useRequestTracker();
    const addEvent = useUiEvent();

    const [redirectPath] = useRedirectPath();

    const { getCaptchaToken, loading } = useGoogleRecaptchaV3();

    const handleSubmit = async ({ email, fullName, privacy, marketing, password }: FormValues) => {
        const token = await getCaptchaToken();

        const consent = {
            marketing_communication: marketing,
            privacy_policy: privacy,
        };
        const { section, curriculumId, module, timezone, timeFormat, weekStart } = registerParams;
        const data = {
            consent,
            email,
            fullName,
            password,
            section,
            curriculum: curriculumId,
            module,
            timezone,
            timeFormat,
            weekStart,
            token,
        };

        void addEvent(actionClick('SignUpButton'));

        return trackRequest(dispatch(createStudent(data)))
            .then(() => {
                trackEverflowRegistration();
                redirectPath ? redirect(redirectPath) : goTo(studentHomePath());
            })
            .catch((errors: FormMessages) => {
                if (isCaptchaError(errors)) {
                    showToast('error', errors.message);
                }
                throw errors;
            });
    };

    return {
        getCaptchaToken,
        loading,
        handleSubmit,
    };
};
